import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api/admin' });

const STORAGE_KEY = 'freejini_admin_token';
const getToken = () => localStorage.getItem(STORAGE_KEY);
const setToken = (t) => localStorage.setItem(STORAGE_KEY, t);
const clearToken = () => localStorage.removeItem(STORAGE_KEY);

const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

const fmt = {
  date: (iso) => iso ? new Date(iso).toLocaleString() : '—',
  dateShort: (iso) => iso ? new Date(iso).toLocaleDateString() : '—',
  num: (n) => n?.toLocaleString() ?? '—',
};

// ── Login ─────────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/login', { username, password });
      setToken(data.token);
      onLogin(data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0c10', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#111318', border: '1px solid #2a2d36', borderRadius: 12, padding: '40px 48px', width: 360 }}>
        <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: '#3b82f6', marginBottom: 4 }}>Freejini</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 28 }}>Admin Portal</div>
        <form onSubmit={submit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: '#9ca3af', display: 'block', marginBottom: 6 }}>USERNAME</label>
            <input value={username} onChange={e => setUsername(e.target.value)} placeholder="geomilano"
              style={{ width: '100%', background: '#0a0c10', border: '1px solid #2a2d36', borderRadius: 6, padding: '10px 12px', fontSize: 14, color: '#e8eaf0', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, color: '#9ca3af', display: 'block', marginBottom: 6 }}>PASSWORD</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              style={{ width: '100%', background: '#0a0c10', border: '1px solid #2a2d36', borderRadius: 6, padding: '10px 12px', fontSize: 14, color: '#e8eaf0', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 16 }}>{error}</div>}
          <button type="submit" disabled={loading}
            style={{ width: '100%', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, padding: '11px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────────
function StatCard({ label, value, sub, color = '#3b82f6' }) {
  return (
    <div style={{ background: '#111318', border: '1px solid #2a2d36', borderRadius: 10, padding: '20px 24px' }}>
      <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color, fontFamily: 'JetBrains Mono, monospace' }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────
function Dashboard() {
  const [stats, setStats] = useState(null);
  const [symbols, setSymbols] = useState([]);

  useEffect(() => {
    api.get('/dashboard', { headers: authHeader() }).then(r => setStats(r.data)).catch(() => {});
    api.get('/analytics/symbols?days=30&limit=15', { headers: authHeader() }).then(r => setSymbols(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20, color: '#e8eaf0' }}>Dashboard</h2>
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
          <StatCard label="Total Users" value={fmt.num(stats.totalUsers)} sub={`+${stats.newToday} today`} />
          <StatCard label="PRO Users" value={fmt.num(stats.proUsers)} color="#22c55e" />
          <StatCard label="Active Today" value={fmt.num(stats.activeToday)} sub={`${stats.activeWeek} this week`} color="#f97316" />
          <StatCard label="Searches Today" value={fmt.num(stats.searchesToday)} color="#a855f7" />
        </div>
      )}

      <div style={{ background: '#111318', border: '1px solid #2a2d36', borderRadius: 10, padding: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>
          Top Searched Symbols — Last 30 Days
        </div>
        {symbols.length === 0 ? (
          <div style={{ color: '#6b7280', fontSize: 13 }}>No data yet — activity will appear as users search.</div>
        ) : (
          <div>
            {symbols.map((s, i) => {
              const max = symbols[0]?.count || 1;
              return (
                <div key={s.symbol} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <div style={{ width: 24, fontSize: 11, color: '#6b7280', textAlign: 'right' }}>#{i + 1}</div>
                  <div style={{ width: 80, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, fontSize: 13, color: '#e8eaf0' }}>{s.symbol}</div>
                  <div style={{ flex: 1, height: 6, background: '#1e2129', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: `${(s.count / max) * 100}%`, height: '100%', background: '#3b82f6', borderRadius: 99 }} />
                  </div>
                  <div style={{ width: 60, fontSize: 12, color: '#9ca3af', textAlign: 'right' }}>{s.count} searches</div>
                  <div style={{ width: 70, fontSize: 12, color: '#6b7280', textAlign: 'right' }}>{s.unique_users} users</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Users ─────────────────────────────────────────────────────
function Users() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [watchlist, setWatchlist] = useState([]);

  const load = useCallback(() => {
    api.get(`/users?search=${encodeURIComponent(search)}&limit=200`, { headers: authHeader() })
      .then(r => setUsers(r.data)).catch(() => {});
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const openUser = (u) => {
    setSelected(u);
    api.get(`/users/${u.id}/watchlist`, { headers: authHeader() }).then(r => setWatchlist(r.data)).catch(() => setWatchlist([]));
  };

  const toggle = async (u, field, val) => {
    await api.patch(`/users/${u.id}`, { [field]: val }, { headers: authHeader() });
    load();
  };

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, color: '#e8eaf0' }}>Users ({users.length})</h2>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by email or name…"
        style={{ width: 300, background: '#111318', border: '1px solid #2a2d36', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#e8eaf0', outline: 'none', marginBottom: 16 }} />

      <div style={{ background: '#111318', border: '1px solid #2a2d36', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2a2d36' }}>
              {['Name', 'Email', 'Joined', 'Last Active', 'Watchlist', 'Searches', 'PRO', 'Status', ''].map(h => (
                <th key={h} style={{ padding: '10px 14px', fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={{ borderBottom: '1px solid #1e2129' }}>
                <td style={{ padding: '10px 14px', fontSize: 13, color: '#e8eaf0', fontWeight: 500 }}>{u.name}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, color: '#9ca3af' }}>{u.email}</td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#6b7280' }}>{fmt.dateShort(u.created_at)}</td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#6b7280' }}>{fmt.dateShort(u.last_active)}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>{u.watchlist_count}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>{u.total_actions}</td>
                <td style={{ padding: '10px 14px' }}>
                  <button onClick={() => toggle(u, 'is_pro', u.is_pro ? 0 : 1)}
                    style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, border: 'none', cursor: 'pointer',
                      background: u.is_pro ? '#22c55e22' : '#1e2129', color: u.is_pro ? '#22c55e' : '#6b7280' }}>
                    {u.is_pro ? 'PRO' : 'Free'}
                  </button>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <button onClick={() => toggle(u, 'is_suspended', u.is_suspended ? 0 : 1)}
                    style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, border: 'none', cursor: 'pointer',
                      background: u.is_suspended ? '#ef444422' : '#1e2129', color: u.is_suspended ? '#ef4444' : '#6b7280' }}>
                    {u.is_suspended ? 'Suspended' : 'Active'}
                  </button>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <button onClick={() => openUser(u)} style={{ fontSize: 12, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer' }}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}
          onClick={() => setSelected(null)}>
          <div style={{ background: '#111318', border: '1px solid #2a2d36', borderRadius: 12, padding: 28, width: 440, maxHeight: '80vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#e8eaf0' }}>{selected.name}</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>{selected.email}</div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              {[
                ['Joined', fmt.dateShort(selected.created_at)],
                ['Last Active', fmt.dateShort(selected.last_active)],
                ['Total Actions', selected.total_actions],
                ['Watchlist Size', selected.watchlist_count],
                ['Plan', selected.is_pro ? 'PRO' : 'Free'],
                ['Status', selected.is_suspended ? 'Suspended' : 'Active'],
              ].map(([k, v]) => (
                <div key={k} style={{ background: '#0a0c10', borderRadius: 6, padding: '10px 12px' }}>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{k}</div>
                  <div style={{ fontSize: 13, color: '#e8eaf0', fontWeight: 500 }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8, fontWeight: 600 }}>WATCHLIST</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {watchlist.length === 0 ? <span style={{ fontSize: 12, color: '#6b7280' }}>Empty</span> :
                watchlist.map(s => (
                  <span key={s} style={{ background: '#1e2129', borderRadius: 4, padding: '3px 8px', fontSize: 12, fontFamily: 'JetBrains Mono, monospace', color: '#9ca3af' }}>{s}</span>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Activity Log ──────────────────────────────────────────────
const ACTION_COLORS = {
  view: '#38bdf8', login: '#22c55e',
  ai_insight: '#a855f7', ai_trading: '#f97316', ai_moat: '#eab308',
  valuation: '#06b6d4', set_alert: '#ef4444', import_portfolio: '#8b5cf6',
};

function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState({ action: '', symbol: '' });

  useEffect(() => {
    const params = new URLSearchParams({ limit: 200 });
    if (filter.action) params.set('action', filter.action);
    if (filter.symbol) params.set('symbol', filter.symbol.toUpperCase());
    api.get(`/activity?${params}`, { headers: authHeader() }).then(r => setLogs(r.data)).catch(() => {});
  }, [filter]);

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, color: '#e8eaf0' }}>Activity Log</h2>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <select value={filter.action} onChange={e => setFilter(f => ({ ...f, action: e.target.value }))}
          style={{ background: '#111318', border: '1px solid #2a2d36', borderRadius: 6, padding: '7px 10px', fontSize: 13, color: '#e8eaf0', outline: 'none' }}>
          <option value="">All Actions</option>
          <option value="view">View Symbol</option>
          <option value="login">Login</option>
          <option value="ai_insight">AI Insight</option>
          <option value="ai_trading">AI Trading Setup</option>
          <option value="ai_moat">Economic Moat</option>
          <option value="valuation">Valuation</option>
          <option value="set_alert">Set Alert</option>
          <option value="import_portfolio">Import Portfolio</option>
        </select>
        <input value={filter.symbol} onChange={e => setFilter(f => ({ ...f, symbol: e.target.value }))}
          placeholder="Filter by symbol…"
          style={{ width: 160, background: '#111318', border: '1px solid #2a2d36', borderRadius: 6, padding: '7px 10px', fontSize: 13, color: '#e8eaf0', outline: 'none' }} />
      </div>

      <div style={{ background: '#111318', border: '1px solid #2a2d36', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2a2d36' }}>
              {['Time', 'User', 'Action', 'Symbol'].map(h => (
                <th key={h} style={{ padding: '10px 14px', fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 && (
              <tr><td colSpan={4} style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: 13 }}>No activity yet</td></tr>
            )}
            {logs.map(l => (
              <tr key={l.id} style={{ borderBottom: '1px solid #1e2129' }}>
                <td style={{ padding: '9px 14px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{fmt.date(l.created_at)}</td>
                <td style={{ padding: '9px 14px', fontSize: 13, color: '#9ca3af' }}>{l.user_name || l.email || <span style={{ color: '#6b7280' }}>Guest</span>}</td>
                <td style={{ padding: '9px 14px' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                    background: `${ACTION_COLORS[l.action] || '#6b7280'}22`, color: ACTION_COLORS[l.action] || '#9ca3af' }}>
                    {l.action}
                  </span>
                </td>
                <td style={{ padding: '9px 14px', fontSize: 13, fontFamily: 'JetBrains Mono, monospace', color: '#e8eaf0' }}>{l.symbol || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Symbol Analytics ──────────────────────────────────────────
function SymbolAnalytics() {
  const [days, setDays] = useState(30);
  const [symbols, setSymbols] = useState([]);

  useEffect(() => {
    api.get(`/analytics/symbols?days=${days}&limit=30`, { headers: authHeader() }).then(r => setSymbols(r.data)).catch(() => {});
  }, [days]);

  const max = symbols[0]?.count || 1;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#e8eaf0' }}>Symbol Analytics</h2>
        {[7, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)}
            style={{ padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
              background: days === d ? '#3b82f6' : '#1e2129', color: days === d ? '#fff' : '#9ca3af' }}>
            {d}d
          </button>
        ))}
      </div>

      {symbols.length === 0 ? (
        <div style={{ color: '#6b7280', fontSize: 13 }}>No search data yet.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div style={{ background: '#111318', border: '1px solid #2a2d36', borderRadius: 10, padding: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>Search Volume</div>
            {symbols.map((s, i) => (
              <div key={s.symbol} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 20, fontSize: 11, color: '#6b7280', textAlign: 'right' }}>#{i + 1}</div>
                <div style={{ width: 72, fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 600, color: '#e8eaf0' }}>{s.symbol}</div>
                <div style={{ flex: 1, height: 8, background: '#1e2129', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ width: `${(s.count / max) * 100}%`, height: '100%', borderRadius: 99,
                    background: i < 3 ? '#3b82f6' : i < 10 ? '#6366f1' : '#374151' }} />
                </div>
                <div style={{ width: 32, fontSize: 12, color: '#9ca3af', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace' }}>{s.count}</div>
              </div>
            ))}
          </div>

          <div style={{ background: '#111318', border: '1px solid #2a2d36', borderRadius: 10, padding: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>Unique Users per Symbol</div>
            {symbols.map((s, i) => {
              const maxU = symbols[0]?.unique_users || 1;
              return (
                <div key={s.symbol} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 20, fontSize: 11, color: '#6b7280', textAlign: 'right' }}>#{i + 1}</div>
                  <div style={{ width: 72, fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 600, color: '#e8eaf0' }}>{s.symbol}</div>
                  <div style={{ flex: 1, height: 8, background: '#1e2129', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: `${(s.unique_users / maxU) * 100}%`, height: '100%', borderRadius: 99,
                      background: i < 3 ? '#22c55e' : i < 10 ? '#16a34a' : '#374151' }} />
                  </div>
                  <div style={{ width: 32, fontSize: 12, color: '#9ca3af', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace' }}>{s.unique_users}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main AdminApp ─────────────────────────────────────────────
const TABS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'users', label: 'Users' },
  { key: 'activity', label: 'Activity Log' },
  { key: 'analytics', label: 'Symbol Analytics' },
];

export default function AdminApp() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState('dashboard');

  useEffect(() => {
    const token = getToken();
    if (!token) { setChecking(false); return; }
    try {
      // Decode JWT payload without verifying signature (server verifies on API calls)
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        clearToken(); // expired
      } else if (payload.is_admin) {
        setUser({ name: payload.name || 'geomilano' });
      } else {
        clearToken(); // not admin
      }
    } catch {
      clearToken();
    }
    setChecking(false);
  }, []);

  const logout = () => { clearToken(); setUser(null); };

  if (checking) return (
    <div style={{ minHeight: '100vh', background: '#0a0c10', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#6b7280', fontSize: 14 }}>Loading…</div>
    </div>
  );

  if (!user) return <AdminLogin onLogin={setUser} />;

  return (
    <div style={{ minHeight: '100vh', background: '#0a0c10', display: 'flex' }}>
      {/* Sidebar */}
      <div style={{ width: 220, background: '#111318', borderRight: '1px solid #2a2d36', display: 'flex', flexDirection: 'column', padding: '24px 0' }}>
        <div style={{ padding: '0 20px 24px', borderBottom: '1px solid #2a2d36' }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 18, fontWeight: 700, color: '#3b82f6' }}>Freejini</div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Admin Portal</div>
        </div>
        <nav style={{ padding: '16px 12px', flex: 1 }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, marginBottom: 2,
                background: tab === t.key ? '#1e2129' : 'transparent', color: tab === t.key ? '#e8eaf0' : '#9ca3af' }}>
              {t.label}
            </button>
          ))}
        </nav>
        <div style={{ padding: '16px 20px', borderTop: '1px solid #2a2d36' }}>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Signed in as <span style={{ color: '#e8eaf0' }}>{user.name}</span></div>
          <button onClick={logout} style={{ fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Sign out</button>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, padding: 32, overflowY: 'auto' }}>
        {tab === 'dashboard' && <Dashboard />}
        {tab === 'users' && <Users />}
        {tab === 'activity' && <ActivityLog />}
        {tab === 'analytics' && <SymbolAnalytics />}
      </div>
    </div>
  );
}
